/**
 * Editor de bloques de People Landing. 21/09/2026 (sprint 4 auditoría): de 1.017 líneas a
 * este diálogo (estado del borrador, validación, guardado); los campos de cada tipo de
 * bloque viven en block-editor/fields/*.tsx y los controles compartidos en block-editor/.
 */
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { isAllowedEmbedUrl } from './BlockRenderer';
import { type LandingBlock, type LandingBlockType, type LandingCardItem, type LandingLinkItem } from '../../../../shared/types/people-landing';
import { emptyDraftFor } from './block-editor/draft';
import { HeroFields } from './block-editor/fields/HeroFields';
import { RichtextFields } from './block-editor/fields/RichtextFields';
import { ImageFields } from './block-editor/fields/ImageFields';
import { CardsFields } from './block-editor/fields/CardsFields';
import { LinksFields } from './block-editor/fields/LinksFields';
import { EmbedFields } from './block-editor/fields/EmbedFields';
import { CalloutFields } from './block-editor/fields/CalloutFields';
import { DynamicFields } from './block-editor/fields/DynamicFields';
import { MarqueeFields } from './block-editor/fields/MarqueeFields';
import { DividerFields } from './block-editor/fields/DividerFields';

interface BlockEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageSlug: string;
  /** Bloque existente (editar) o null (crear con createType). */
  block: LandingBlock | null;
  createType?: LandingBlockType;
  imageUrls: Record<string, string>;
  onSave: (block: LandingBlock) => void;
  isSaving?: boolean;
}

export function BlockEditorDialog({
  open,
  onOpenChange,
  pageSlug,
  block,
  createType,
  imageUrls,
  onSave,
  isSaving,
}: BlockEditorDialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<any>(null);

  useEffect(() => {
    if (!open) return;
    if (block) setDraft(JSON.parse(JSON.stringify(block)));
    else if (createType) setDraft(emptyDraftFor(createType));
  }, [open, block, createType]);

  if (!draft) return null;

  const set = (patch: Record<string, unknown>) => setDraft((d: any) => ({ ...d, ...patch }));
  const setItem = (idx: number, patch: Record<string, unknown>) =>
    setDraft((d: any) => ({
      ...d,
      items: d.items.map((it: any, i: number) => (i === idx ? { ...it, ...patch } : it)),
    }));
  const removeItem = (idx: number) =>
    setDraft((d: any) => ({ ...d, items: d.items.filter((_: any, i: number) => i !== idx) }));
  const moveItem = (idx: number, dir: -1 | 1) =>
    setDraft((d: any) => {
      const items = [...d.items];
      const j = idx + dir;
      if (j < 0 || j >= items.length) return d;
      [items[idx], items[j]] = [items[j], items[idx]];
      return { ...d, items };
    });

  const clean = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);

  const handleSave = () => {
    const type: LandingBlockType = draft.type;
    if (type === 'richtext' && !draft.markdown?.trim()) {
      toast.error(t('people_landing.editor.markdown_required', 'El texto no puede estar vacío'));
      return;
    }
    if (type === 'image' && !draft.imageKey) {
      toast.error(t('people_landing.editor.image_required', 'Subí una imagen'));
      return;
    }
    if (type === 'embed') {
      if (!draft.url?.trim()) {
        toast.error(t('people_landing.editor.url_required', 'Ingresá una URL'));
        return;
      }
      if (!isAllowedEmbedUrl(draft.url.trim())) {
        toast.error(
          t(
            'people_landing.editor.embed_not_allowed',
            'Solo se pueden embeber YouTube (/embed/) o Drive/Docs (/preview)'
          )
        );
        return;
      }
    }
    if (type === 'callout' && !draft.title?.trim()) {
      toast.error(t('people_landing.editor.title_required', 'Ingresá un título'));
      return;
    }
    if (type === 'marquee' && !(draft.imageKeys?.filter(Boolean).length > 0)) {
      toast.error(t('people_landing.editor.marquee_images_required', 'Subí al menos una imagen'));
      return;
    }
    if (type === 'hero' && !draft.title?.trim()) {
      toast.error(t('people_landing.editor.title_required', 'Ingresá un título'));
      return;
    }
    if ((type === 'links' || type === 'cards') && draft.items.length === 0) {
      toast.error(t('people_landing.editor.items_required', 'Agregá al menos un ítem'));
      return;
    }
    if (type === 'links' && draft.items.some((i: LandingLinkItem) => !i.title?.trim() || !i.url?.trim())) {
      toast.error(t('people_landing.editor.link_fields_required', 'Cada link necesita título y URL'));
      return;
    }
    if (
      type === 'cards' &&
      draft.items.some((i: LandingCardItem) => !i.title?.trim() && !i.text?.trim() && !i.imageKey)
    ) {
      toast.error(
        t('people_landing.editor.card_fields_required', 'Cada tarjeta necesita al menos título, texto o imagen')
      );
      return;
    }

    const base = { ...draft, id: draft.id ?? uuidv4() };
    let result: LandingBlock;
    const layout = { span: draft.span, rowSpan: draft.rowSpan };
    switch (type) {
      case 'hero':
        result = {
          id: base.id,
          type,
          title: draft.title.trim(),
          subtitle: clean(draft.subtitle) as string | undefined,
          imageKey: draft.imageKey,
          ctaLabel: clean(draft.ctaLabel) as string | undefined,
          ctaUrl: clean(draft.ctaUrl) as string | undefined,
          // Presets (Fase 2): solo se guardan si difieren del default, así el doc queda chico.
          style: draft.style && draft.style !== 'classic' ? draft.style : undefined,
          mood: draft.mood === 'bold' ? 'bold' : undefined,
          align: draft.align && draft.align !== 'center' ? draft.align : undefined,
          size: draft.size && draft.size !== 'md' ? draft.size : undefined,
          eyebrow: clean(draft.eyebrow) as string | undefined,
          ctaSecondaryLabel: clean(draft.ctaSecondaryLabel) as string | undefined,
          ctaSecondaryUrl: clean(draft.ctaSecondaryUrl) as string | undefined,
        };
        break;
      case 'richtext':
        result = {
          id: base.id,
          type,
          title: clean(draft.title) as string | undefined,
          markdown: draft.markdown,
          align: draft.align && draft.align !== 'left' ? draft.align : undefined,
        };
        break;
      case 'image':
        result = {
          id: base.id,
          type,
          imageKey: draft.imageKey,
          caption: clean(draft.caption) as string | undefined,
          url: clean(draft.url) as string | undefined,
          size: draft.size,
        };
        break;
      case 'cards':
        result = {
          id: base.id,
          type,
          title: clean(draft.title) as string | undefined,
          columns: draft.columns,
          align: draft.align && draft.align !== 'left' ? draft.align : undefined,
          items: draft.items.map((it: LandingCardItem) => ({
            id: it.id ?? uuidv4(),
            title: clean(it.title) as string | undefined,
            text: clean(it.text) as string | undefined,
            imageKey: it.imageKey,
            url: clean(it.url) as string | undefined,
          })),
        };
        break;
      case 'links':
        result = {
          id: base.id,
          type,
          title: clean(draft.title) as string | undefined,
          items: draft.items.map((it: LandingLinkItem) => ({
            id: it.id ?? uuidv4(),
            title: it.title.trim(),
            url: it.url.trim(),
            description: clean(it.description) as string | undefined,
          })),
        };
        break;
      case 'embed':
        result = {
          id: base.id,
          type,
          title: clean(draft.title) as string | undefined,
          url: draft.url.trim(),
        };
        break;
      case 'callout':
        result = {
          id: base.id,
          type,
          eyebrow: clean(draft.eyebrow) as string | undefined,
          title: draft.title.trim(),
          text: clean(draft.text) as string | undefined,
          tone: draft.tone ?? 'purple',
          url: clean(draft.url) as string | undefined,
        };
        break;
      case 'team':
      case 'progress':
      case 'news':
        result = { id: base.id, type, title: clean(draft.title) as string | undefined };
        break;
      case 'video':
        if (draft.url?.trim() && !isAllowedEmbedUrl(draft.url.trim())) {
          toast.error(t('people_landing.editor.embed_not_allowed', 'Solo se pueden embeber YouTube (/embed/) o Drive/Docs (/preview)'));
          return;
        }
        result = { id: base.id, type, title: clean(draft.title) as string | undefined, url: clean(draft.url) as string | undefined };
        break;
      case 'marquee':
        result = {
          id: base.id,
          type,
          title: clean(draft.title) as string | undefined,
          imageKeys: (draft.imageKeys as (string | undefined)[]).filter((k): k is string => !!k).slice(0, 12),
        };
        break;
      case 'divider':
        result = { id: base.id, type };
        break;
    }
    onSave({ ...layout, ...result });
  };

  const typeLabels: Record<LandingBlockType, string> = {
    hero: t('people_landing.blocks.hero', 'Portada'),
    richtext: t('people_landing.blocks.richtext', 'Texto'),
    image: t('people_landing.blocks.image', 'Imagen'),
    cards: t('people_landing.blocks.cards', 'Tarjetas'),
    links: t('people_landing.blocks.links', 'Botones / Links'),
    embed: t('people_landing.blocks.embed', 'Video / Documento'),
    divider: t('people_landing.blocks.divider', 'Separador'),
    callout: t('people_landing.blocks.callout', 'Destacado'),
    team: t('people_landing.blocks.team', 'Tu equipo (automático)'),
    progress: t('people_landing.blocks.progress', 'Tu primer mes (automático)'),
    news: t('people_landing.blocks.news', 'Novedades (automático)'),
    video: t('people_landing.blocks.video', 'Video de bienvenida'),
    marquee: t('people_landing.blocks.marquee', 'Fotos en movimiento'),
  };

  const fieldProps = { draft, set, setDraft, setItem, removeItem, moveItem, imageUrls, pageSlug };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {block
              ? t('people_landing.editor.edit_title', 'Editar sección')
              : t('people_landing.editor.new_title', 'Nueva sección')}
            {' · '}
            {typeLabels[draft.type as LandingBlockType]}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {draft.type === 'hero' && <HeroFields {...fieldProps} />}

          {draft.type === 'richtext' && <RichtextFields {...fieldProps} />}

          {draft.type === 'image' && <ImageFields {...fieldProps} />}

          {draft.type === 'cards' && <CardsFields {...fieldProps} />}

          {draft.type === 'links' && <LinksFields {...fieldProps} />}

          {draft.type === 'embed' && <EmbedFields {...fieldProps} />}

          {draft.type === 'callout' && <CalloutFields {...fieldProps} />}

          {(draft.type === 'team' || draft.type === 'progress' || draft.type === 'news' || draft.type === 'video') && <DynamicFields {...fieldProps} />}

          {draft.type === 'marquee' && <MarqueeFields {...fieldProps} />}

          {draft.type === 'divider' && <DividerFields {...fieldProps} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.ui.cancel', 'Cancelar')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.ui.save', 'Guardar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
